import React, { useEffect, useRef, useState } from "react";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUpload, resolveMediaUrl, uploadMediaFile } from "@/components/FileUpload";
import { Switch } from "@/components/ui/switch";
import { Music, Plus, Trash2, Play, Pause, Loader2, Upload, Star, Image as ImageIcon, Circle } from "lucide-react";
import { toast } from "sonner";

// Super-admin managed music library — the "sounds" offered when adding
// background music to a Story or a post (image / video). Built-in defaults are
// seeded server-side and flagged so admins know they are the shipped clips.
export const MusicPanel = () => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [autoFeature, setAutoFeature] = useState(false);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/music"); setTracks(data.tracks || []); setAutoFeature(!!data.auto_feature); }
    catch { toast.error("Erreur de chargement de la bibliothèque"); }
    setLoading(false);
  };
  useEffect(() => { load(); return () => { try { audioRef.current?.pause(); } catch { /* noop */ } }; }, []);

  const toggleRotation = async (enabled) => {
    setAutoFeature(enabled);
    try {
      await api.patch("/admin/music-rotation", { enabled });
      toast.success(enabled ? "Rotation automatique activée ⭐" : "Rotation automatique désactivée");
      if (enabled) load(); // reflect the freshly auto-featured track
    } catch (e) { setAutoFeature(!enabled); toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const handleFile = async (files) => {
    const f = files && files[0];
    if (!f) return;
    setUploading(true);
    try {
      const data = await uploadMediaFile(f, { context: "feed" });
      setUrl(data.url);
      if (!title.trim()) setTitle(f.name.replace(/\.[^.]+$/, "").slice(0, 60));
      toast.success("Fichier importé ♪");
    } catch (e) { toast.error(e?.friendlyMessage || "Échec de l'import du fichier"); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!title.trim()) { toast.error("Indique un titre"); return; }
    if (!url.trim()) { toast.error("Ajoute un fichier MP3 ou une URL"); return; }
    setSaving(true);
    try {
      await api.post("/admin/music", { title: title.trim(), url: url.trim() });
      toast.success("Morceau ajouté 🎵");
      setTitle(""); setUrl("");
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setSaving(false); }
  };

  const remove = async (tk) => {
    if (!window.confirm(`Supprimer « ${tk.title} » de la bibliothèque ?`)) return;
    try {
      await api.delete(`/admin/music/${tk.id}`);
      setTracks((arr) => arr.filter((x) => x.id !== tk.id));
      toast.success("Morceau supprimé");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const toggleFeatured = async (tk) => {
    const next = !tk.featured;
    try {
      await api.patch(`/admin/music/${tk.id}`, { featured: next });
      // Single "à la une": setting one clears the others.
      setTracks((arr) => arr.map((x) => ({ ...x, featured: x.id === tk.id ? next : (next ? false : x.featured) })));
      toast.success(next ? "Mis à la une ⭐" : "Retiré de la une");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const togglePreview = (tk) => {
    const a = audioRef.current;
    if (!a) return;
    if (preview === tk.id) { a.pause(); setPreview(null); return; }
    a.src = resolveMediaUrl(tk.url); a.currentTime = 0; a.play().catch(() => {}); setPreview(tk.id);
  };

  if (loading) return <AdminSkeleton />;

  return (
    <div className="space-y-6" data-testid="admin-music-panel">
      <div>
        <h2 className="text-xl font-bold flex items-center gap-2"><Music size={20} style={{ color: "var(--yalola-primary)" }} /> Bibliothèque musicale</h2>
        <p className="text-sm mt-1" style={{ color: "var(--yalola-text-muted)" }}>
          Les sons proposés pour ajouter une musique de fond aux stories et aux publications (photos / vidéos).
        </p>
      </div>

      {/* Weekly auto-rotation of the featured sound */}
      <div className="yalola-card p-4 flex items-center justify-between gap-3" data-testid="admin-music-rotation">
        <div>
          <div className="text-sm font-semibold flex items-center gap-1.5"><Star size={15} className="text-amber-400 fill-amber-400" /> Rotation automatique</div>
          <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
            Chaque lundi, le son le plus utilisé passe automatiquement « À la une ».
          </div>
        </div>
        <Switch checked={autoFeature} onCheckedChange={toggleRotation} data-testid="admin-music-rotation-toggle" />
      </div>

      {/* Add a track */}
      <div className="yalola-card p-4 space-y-3" data-testid="admin-music-add">
        <div className="text-sm font-semibold">Ajouter un morceau</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex. Adoration" maxLength={80} data-testid="admin-music-title" />
          </div>
          <div>
            <Label className="text-xs">Fichier ou URL</Label>
            <div className="flex items-center gap-2">
              <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://… ou importez un MP3" data-testid="admin-music-url" />
              <FileUpload accept="audio/*" onFiles={handleFile} variant="custom" testId="admin-music-upload" context="feed">
                <span className="inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold whitespace-nowrap" style={{ borderColor: "var(--yalola-border)" }}>
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} MP3
                </span>
              </FileUpload>
            </div>
          </div>
        </div>
        <Button className="pill-primary" onClick={add} disabled={saving || uploading} data-testid="admin-music-add-btn">
          {saving ? <Loader2 size={15} className="animate-spin mr-1" /> : <Plus size={15} className="mr-1" />} Ajouter
        </Button>
      </div>

      {/* Library list */}
      <div className="space-y-2" data-testid="admin-music-list">
        {tracks.length === 0 && (
          <div className="text-sm text-center py-8" style={{ color: "var(--yalola-text-muted)" }}>Aucun morceau pour l'instant.</div>
        )}
        {tracks.map((tk) => (
          <div key={tk.id} className="yalola-card p-3 flex items-center gap-3" data-testid={`admin-music-row-${tk.id}`}>
            <button
              type="button"
              onClick={() => togglePreview(tk)}
              className="h-9 w-9 shrink-0 grid place-items-center rounded-full text-white"
              style={{ background: "var(--yalola-primary)" }}
              data-testid={`admin-music-preview-${tk.id}`}
            >
              {preview === tk.id ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate flex items-center gap-2">
                {tk.title}
                {tk.featured && (
                  <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 inline-flex items-center gap-1" style={{ background: "rgba(245,158,11,0.15)", color: "#b45309" }}>
                    <Star size={10} className="fill-amber-400 text-amber-400" /> À la une
                  </span>
                )}
                {tk.builtin && (
                  <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>
                    Par défaut
                  </span>
                )}
              </div>
              <div className="text-xs truncate flex items-center gap-2.5 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                <span className="truncate max-w-[220px]">{tk.url}</span>
                <span className="shrink-0 inline-flex items-center gap-2 text-[11px] font-semibold" data-testid={`admin-music-uses-${tk.id}`}>
                  <span className="inline-flex items-center gap-1" title="Publications" style={{ color: "var(--yalola-primary)" }}>
                    <ImageIcon size={11} /> {tk.uses_posts || 0}
                  </span>
                  <span className="inline-flex items-center gap-1" title="Stories" style={{ color: "#d97706" }}>
                    <Circle size={11} /> {tk.uses_stories || 0}
                  </span>
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleFeatured(tk)}
              className="h-9 w-9 shrink-0 grid place-items-center rounded-full hover:bg-amber-50"
              style={{ color: tk.featured ? "#f59e0b" : "var(--yalola-text-muted)" }}
              data-testid={`admin-music-feature-${tk.id}`}
              title={tk.featured ? "Retirer de la une" : "Mettre à la une"}
            >
              <Star size={16} className={tk.featured ? "fill-amber-400" : ""} />
            </button>
            <button
              type="button"
              onClick={() => remove(tk)}
              className="h-9 w-9 shrink-0 grid place-items-center rounded-full text-red-600 hover:bg-red-50"
              data-testid={`admin-music-delete-${tk.id}`}
              title="Supprimer"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <audio ref={audioRef} onEnded={() => setPreview(null)} className="hidden" />
    </div>
  );
};

export default MusicPanel;
